import React, { useContext, useEffect } from 'react';
import {
  CustomField,
  CustomFieldType,
  CustomFieldValue,
  WithCustomFields,
} from '../../../types/custom_field/customField';
import { Grid } from '@mui/material';
import DatePicker from '../../components/DatePicker/DatePicker';
import DropdownSelect from './DropdownSelect';
import { FileContext } from '../../../context/FileContext';
import FileInput from './FileInput';
import { FileEntityType } from '../../../types/file';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Checkbox } from '../../components/Checkbox';
import { TextInput } from '../../components/TextInput';
import { DropdownTextSelect, DropdownSearchSelect, DropdownItem } from '../../components/Dropdown';
import { CustomFieldUtil } from '../../../types/custom_field';
import { User } from '../../../types/user';
import { Contact } from '../../../types/contact';
import CustomFieldInputImage from '../../components/CustomFIeldInputImage/CustomFieldInputImage';
import { ProductMasterData } from '../../../types/product_master_data/productMasterData';
import { UnitOfMeasureUtil } from '../../../types/uom';
import { v4 } from 'uuid';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { translate } from '../../../types/translation/Translator';

interface CustomFieldInputProps {
  item: CustomField | undefined;
  value: any;
  entity: WithCustomFields;
  onChange: React.Dispatch<React.SetStateAction<any>>;
  withCustomField?: (id: string, value: CustomFieldValue | null) => WithCustomFields;
  disabled?: boolean;
  parent?: CustomFieldValue;
  pmdId?: string;
  name?: string;
  index?: number;
  generated?: boolean;
  id?: string;

  users: Map<string, User>;
  contacts: Map<string, Contact>;
  productMasterData: Map<string, ProductMasterData>;
}

export default function CustomFieldInput({
  value,
  item,
  entity,
  onChange,
  withCustomField,
  disabled = false,
  pmdId,
  name,
  index,
  generated,
  parent,
  id,
  users,
  contacts,
  productMasterData,
}: CustomFieldInputProps) {
  if (!item) return null;

  const { files } = useContext(FileContext);

  const label = name || item.name;

  const onValueChange = (id: string, value: CustomFieldValue | null) => {
    if (withCustomField) {
      onChange(withCustomField(id, value));
    } else {
      onChange(entity.withCustomField(value, id));
    }
  };

  useEffect(() => {
    if (!(entity as any).id && !value && item.defaultValue) {
      onValueChange(item.id, CustomField.toValue(item, CustomField.getDefaultValue(item)));
    }
  }, []);

  const handleChange = (item: CustomField, value: any) => {
    switch (item.type) {
      case CustomFieldType.text:
      case CustomFieldType.number:
        return onValueChange(item.id, {
          ...item,
          id: id || item.id,
          name: name || item.name,
          generated: generated || item.generated,
          index: index || item.index,
          value: String(value),
        });
      case CustomFieldType.bool:
        return onValueChange(item.id, { ...item, value: String(value) });
      case CustomFieldType.date:
        return onValueChange(item.id, { ...item, value });
      case CustomFieldType.listOfValues:
        if (value === null) {
          return onValueChange(item.id, null);
        } else {
          return onValueChange(item.id, { ...item, value: String(value) });
        }
      case CustomFieldType.file:
        const current = entity.customFields.get(item.id)?.value;
        if (current) {
          // TODO: Change to ::
          return onValueChange(item.id, { ...item, value: `${current};;${value}` });
        } else {
          return onValueChange(item.id, { ...item, value: String(value) });
        }
      case CustomFieldType.listOfTuples:
        if (!value) {
          return onValueChange(item.id, null);
        } else {
          return onValueChange(item.id, { ...item, value: String(value) });
        }
      case CustomFieldType.image:
        if (!value) {
          return onValueChange(item.id, null);
        } else {
          return onValueChange(item.id, { ...item, value: String(value) });
        }
    }
  };

  // if (disabled && (value === '' || value == null || value == undefined)) return null;

  switch (item.type) {
    case CustomFieldType.text:
      let prefix = '';
      if (parent && parent.type === CustomFieldType.listOfTuples) {
        prefix = parent.value?.split(':::')[1] || '';
      }

      if (item.reference) {
        const items = CustomFieldUtil.getReferenceItems(item, users, contacts, productMasterData);

        return (
          <DropdownTextSelect
            value={String(value).includes(prefix) ? String(value || '') : `${prefix}${String(value || '')}`}
            onSelect={v => {
              if (prefix && !String(v).includes(prefix)) {
                handleChange(item, prefix);
              } else {
                handleChange(item, v);
              }
            }}
            placeholder={label || item.name}
            label={label || item.name}
            items={items}
            textBuilder={v => v}
            lastBuilder={options => {
              if (!options.input) return null;

              return (
                <DropdownItem key={v4()} selected={options.selected} onClick={options.closeDropdown}>
                  <div>
                    {translate({ key: TranslationKey.useCustomValue })}: {options.input}
                  </div>
                </DropdownItem>
              );
            }}
            disabled={disabled || item.generated}
            mandatory={item.mandatory}
          />
        );
      }

      return (
        <TextInput
          disabled={disabled}
          placeholder={label || item.name}
          label={label || item.name}
          mandatory={item.mandatory}
          trailing={UnitOfMeasureUtil.getSuffix(item.unitOfMeasure)}
          value={String(value).includes(prefix) ? String(value || '') : `${prefix}${String(value || '')}`}
          onChange={v => {
            if (prefix && !String(v).includes(prefix)) {
              handleChange(item, prefix);
            } else {
              handleChange(item, v);
            }
          }}
        />
      );
    case CustomFieldType.bool:
      return (
        <Checkbox
          disabled={disabled || item.generated}
          label={label || item.name}
          value={value === 'true' || false}
          onChange={v => handleChange(item, v)}
        />
      );
    case CustomFieldType.date:
      return (
        <DatePicker
          disabled={disabled || item.generated}
          label={label || item.name}
          placeholder={label || item.name}
          mandatory={item.mandatory}
          value={value}
          onChange={(val?: string) => handleChange(item, val)}
        />
      );
    case CustomFieldType.listOfValues:
      let values = item.values;
      if (item.reference) values = CustomFieldUtil.getReferenceItems(item, users, contacts, productMasterData);

      return (
        <DropdownSearchSelect
          mandatory={item.mandatory}
          label={label || item.name}
          placeholder={label || item.name}
          items={values}
          value={value || null}
          textBuilder={item => item || ''}
          onSelect={v => handleChange(item, v || '')}
          disabled={disabled || item.generated}
        />
      );
    case CustomFieldType.number:
      return (
        <TextInput
          type='number'
          disabled={disabled}
          label={label || item.name}
          placeholder={label || item.name}
          mandatory={item.mandatory}
          value={value || ''}
          onChange={v => handleChange(item, v)}
          trailing={UnitOfMeasureUtil.getSuffix(item.unitOfMeasure)}
        />
      );
    case CustomFieldType.file:
      const cfValue = entity.customFields.get(item.id)?.value;
      const attached = new Set(cfValue?.split(';;'));
      if (disabled) {
        if (!attached?.size) return null;
        return (
          <>
            <p className='mt-1 text-sm font-medium text-slate-800'>{item.name}</p>
            {[...attached.values()]
              .map(id => files.get(id))
              .map(file => (
                <FileInput file={file} />
              ))}
          </>
        );
      }

      const items = [...files.values()].filter(
        file => file.entityType === FileEntityType.pmd && file.entityId === pmdId,
      );

      return (
        <>
          <DropdownSelect
            mandatory={item.mandatory}
            label={label || item.name}
            placeholder={label || item.name}
            selectOnly
            values={items.filter(file => !attached.has(file.id))}
            selectedValue={null}
            toText={item => item.name || ''}
            onChange={v => (v ? handleChange(item, v.id) : null)}
            disabled={disabled}
          />
          <div className='py-1'></div>
          {cfValue && attached?.size
            ? [...attached.values()]
                .map(id => files.get(id))
                .map(file => (
                  <Grid container>
                    <Grid
                      item
                      alignContent={'center'}
                      className='cursor-pointer hover:text-red-500'
                      onClick={() => {
                        const current = entity.customFields.get(item.id);
                        if (current) {
                          const items = new Set(current.value.split(';;'));
                          items.delete(file?.id || '');
                          return onChange(entity.withCustomField({ ...item, value: [...items.values()].join(';;') }));
                        }
                      }}
                    >
                      <CloseOutlinedIcon />
                    </Grid>
                    <Grid item flexGrow={1}>
                      <FileInput file={file} icon={false} />
                    </Grid>
                  </Grid>
                ))
            : null}
        </>
      );
    case CustomFieldType.listOfTuples:
      return (
        <DropdownSearchSelect
          mandatory={item.mandatory}
          label={label || item.name}
          placeholder={label || item.name}
          items={item.values}
          value={value}
          textBuilder={item => item.split(':::')[0] || ''}
          onSelect={v => handleChange(item, v)}
          disabled={disabled}
        />
      );
    case CustomFieldType.image:
      const fileEntityType = CustomFieldUtil.getFileEntityType(item);
      if (!fileEntityType) return null;

      return (
        <Grid container>
          <Grid xs={6}>
            <p className='mb-2 text-sm font-medium text-slate-800'>{item.name}</p>
            <CustomFieldInputImage
              entity={entity}
              entityType={fileEntityType}
              disabled={false}
              item={item}
              handleChange={handleChange}
            />
          </Grid>
        </Grid>
      );
  }
}
